import React, { useState, useEffect } from "react";
import { withTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import * as moment from "moment";
import PropTypes from "prop-types";
import DatePicker from "react-multi-date-picker";
import dayjs, { Dayjs } from "dayjs";
import ClipLoader from "react-spinners/ClipLoader";
import { Button, TextInput, Image, Dropdown } from "hesus-ui-kit";
import {
  getDatePickerFormat,
  getDateFormat,
  getCalendarLocale,
} from "@utils/datePickerUtils";
import { useGetGeneralParameters } from "@hooks/useGeneralParams";
import { addSingleSite } from "../../../SiteList/SiteList.slice";
import { initMap as initMapDrag } from "@utils/mapSingleMarkerDragged";
import addIcon from "@icons/ic-add-green.svg";
import mapBlueIcon from "@icons/ic-map-marker-blue.svg";
import enableIcon from "@icons/ic-enable.svg";
import disableIcon from "@icons/ic-disable.svg";
import AddEditWorkingHours from "../../../../WorkingHours/AddEditWorkingHours";
import AddEditRelatedContact from "../../../../RelatedContact/AddEditRelatedContact/AddEditRelatedContact";
import DocumentUploadWithType from "@components/DocumentUpload/DocumentUploadWithType";
import AddressSearchWithMap from "@components/AddressSearch/AddressSearchWithMap";
import ParentCompany from "../../../../Company/AddEditCompany/components/ParentCompany/ParentCompany";
import CKEditorText from "@components/CKEditorText/CKEditorText";
import { prefixRegistration } from "@utils/identificationRule";
import { getCountryName } from "@utils/utils";
import { siteDocumentTypes } from "@utils/constants";
import {
  labelStyles,
  inputStyles,
  labelStylesError,
  inputStylesError,
  RequiredMessage,
} from "@utils/extra-style";
import UserInfo from "@utils/UserInfo";
import "@utils/extra-style.scss";

import {
  ButtonWrapper,
  FormBody,
  SectionContainer,
  OuterWrapper,
  FlexRow,
  GridRow,
  OuterWrapperBackground,
  PaddingDiv,
  ContainerLabel,
  MapContainer,
  ParentDatePicker,
  ContactButton,
  ContactContainer,
  SpanButton,
} from "./Site.styled";
import "../../AddEditSite.scss";

const Site = (props) => {
  const {
    t,
    id,
    selectedSite,
    setSelectedSite,
    isEditMode,
    setActiveTab,
    isLeadJobsite,
    leadId,
  } = props;

  const history = useNavigate();
  const dispatch = useDispatch();
  const generalParams = useGetGeneralParameters();
  const queryParams = new URLSearchParams(window.location.search);
  const queryParamType = queryParams.get("parent");
  const [isSiteCreating, setIsSiteCreating] = useState(false);
  const [createSiteAPIError, setCreateSiteAPIError] = useState("");
  const [position, setPosition] = useState();
  const [isLeadCompanyUpdated, setLeadCompanyUpdated] = useState(false);
  const [resetMapAddress, setResetMapAddress] = useState(false);
  const [resetParentCompany, setResetParentCompany] = useState(false);
  const [adminComment, setAdminComment] = useState(
    selectedSite?.adminComment ?? ""
  );
  const [isEditContactPopupVisible, setIsEditContactPopupVisible] =
    useState(false);
  const [existingDocuments, setExistingDocuments] = useState([]);
  const [docType, setDocType] = useState([]);
  const [allCompanyList, setAllCompanyList] = useState([]);

  const userInfo = UserInfo.getUser();
  const countryInfo = UserInfo.countryInfo();
  const countryListForFilter = UserInfo.countryListForFilter(t);

  const [inputList, setInputList] = useState([
    {
      id: "",
      openingTime: dayjs("0000-00-00T07:00"),
      closingTime: dayjs("0000-00-00T16:00"),
      days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    },
  ]);

  useEffect(() => {
    if (isLeadJobsite) {
      setInputList([
        {
          id: "",
          openingTime: selectedSite?.openingHour
            ? dayjs("0000-00-00T" + selectedSite?.openingHour)
            : dayjs("0000-00-00T07:00"),
          closingTime: selectedSite?.closingHour
            ? dayjs("0000-00-00T" + selectedSite?.closingHour)
            : dayjs("0000-00-00T16:00"),
          days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        },
      ]);
    }
  }, [isLeadJobsite]);

  const [inputListError, setInputListError] = useState([
    {
      timeError: "",
    },
  ]);

  const [requiredTimeData, setRequiredTimeData] = useState("");
  const [countryListAvailable, setCountryListAvailable] = useState(false);

  const defaultCoordinates = generalParams.coordinates;

  const countries = generalParams.countryList;

  const countryId = countryInfo.country.id;

  const [contactList, setContactList] = useState([]);

  const addContactToList = (data) => {
    setContactList([...contactList, { ...data }]);
  };

  useEffect(() => {
    setFormData({
      ...formData,
      contacts: {
        value: contactList,
        error: "",
        required: true,
      },
    });
  }, [contactList]);

  const addContact = () => {
    setIsEditContactPopupVisible(true);
  };

  const closeContactEditPopup = () => {
    setIsEditContactPopupVisible(false);
  };

  useEffect(() => {
    if (!!countries && Object.keys(countries).length > 0) {
      setCountryListAvailable(true);
    }
  }, [countries]);

  const [formData, setFormData] = useState({
    id: {
      required: false,
      value: selectedSite?.id ?? null,
      error: "",
    },
    name: {
      required: true,
      value: selectedSite?.name ?? "",
      error: "",
    },
    isTender: {
      required: true,
      value: selectedSite?.isTender ?? false,
      error: "",
    },
    quoteStatus: {
      required: false,
      value: 11,
      error: "",
    },
    company: {
      required: true,
      value:
        {
          id: selectedSite?.company?.id,
          name: selectedSite?.company?.id + " - " + selectedSite?.company?.name,
          registrationNumber: prefixRegistration(
            formData?.country?.value,
            selectedSite?.company?.identificationNumber
          ),
        } ?? "",
      error: "",
    },
    startDate: {
      required: true,
      value: selectedSite?.estimatedStartDate
        ? moment(selectedSite.estimatedStartDate).format("YYYY-MM-DD")
        : null,
      error: "",
    },
    endDate: {
      required: true,
      value: selectedSite?.estimatedEndDate
        ? moment(selectedSite.estimatedEndDate).format("YYYY-MM-DD")
        : null,
      error: "",
    },
    forecastClosingDate: {
      required: true,
      value: selectedSite?.expectedClosingDate
        ? moment(selectedSite.expectedClosingDate).format("YYYY-MM-DD")
        : null,
      error: "",
    },
    contacts: {
      required: true,
      value: [],
      error: "",
    },
    address: {
      required: true,
      value: selectedSite?.address ?? "",
      error: "",
    },
    addressSupplement: {
      required: false,
      value: selectedSite?.address?.supplement ?? "",
      error: "",
    },
    adminComment: {
      required: false,
      value: selectedSite?.adminComment ?? "",
      error: "",
    },
    loadingTime: {
      required: true,
      value: selectedSite?.loadingTime ?? 15,
      error: "",
    },
    unloadingTime: {
      required: true,
      value: selectedSite?.unloadingTime ?? 15,
      error: "",
    },
    simultaneousLoads: {
      required: true,
      value: selectedSite?.simultaneousLoads ?? 1,
      error: "",
    },
    simultaneousUnloads: {
      required: true,
      value: selectedSite?.simultaneousUnloads ?? 1,
      error: "",
    },
    workingHours: {
      required: false,
      value: selectedSite?.workingHours ?? [],
      error: "",
    },
    country: {
      required: true,
      value: selectedSite?.platformCountry?.id ?? countryId,
      error: "",
    },
    documents: [],
  });
  useEffect(() => {
    setFormData({
      id: {
        required: false,
        value: selectedSite?.id ?? null,
        error: "",
      },
      name: {
        required: true,
        value: selectedSite?.name ?? "",
        error: "",
      },
      isTender: {
        required: true,
        value: selectedSite?.isTender ?? false,
        error: "",
      },
      company: {
        required: true,
        value:
          {
            id: selectedSite?.company?.id,
            name:
              selectedSite?.company?.id + " - " + selectedSite?.company?.name,
            registrationNumber: prefixRegistration(
              formData?.country?.value,
              selectedSite?.company?.identificationNumber
            ),
          } ?? "",
        error: "",
      },
      startDate: {
        required: true,
        value: selectedSite?.estimatedStartDate
          ? moment(selectedSite.estimatedStartDate).format("YYYY-MM-DD")
          : null,
        error: "",
      },
      endDate: {
        required: true,
        value: selectedSite?.estimatedEndDate
          ? moment(selectedSite.estimatedEndDate).format("YYYY-MM-DD")
          : null,
        error: "",
      },
      forecastClosingDate: {
        required: true,
        value: selectedSite?.expectedClosingDate
          ? moment(selectedSite.expectedClosingDate).format("YYYY-MM-DD")
          : null,
        error: "",
      },
      address: {
        required: true,
        value: selectedSite?.address ?? "",
        error: "",
      },
      addressSupplement: {
        required: false,
        value: selectedSite?.address?.supplement ?? "",
        error: "",
      },
      adminComment: {
        required: false,
        value: selectedSite?.adminComment ?? "",
        error: "",
      },
      loadingTime: {
        required: true,
        value: selectedSite?.loadingTime ?? 15,
        error: "",
      },
      unloadingTime: {
        required: true,
        value: selectedSite?.unloadingTime ?? 15,
        error: "",
      },
      simultaneousLoads: {
        required: true,
        value: selectedSite?.simultaneousLoads ?? 1,
        error: "",
      },
      simultaneousUnloads: {
        required: true,
        value: selectedSite?.simultaneousUnloads ?? 1,
        error: "",
      },
      workingHours: {
        required: false,
        value: selectedSite?.workingHours ?? [],
        error: "",
      },
      country: {
        required: true,
        value: selectedSite?.platformCountry?.id ?? countryId,
        error: "",
      },
      documents: [],
    });
    setAdminComment(selectedSite?.adminComment);
    const leadConatct = [];
    if (leadId && selectedSite?.contact) {
      leadConatct.push({
        contact: {
          id: selectedSite?.contact?.id,
          name:
            selectedSite?.contact?.id +
            "-" +
            selectedSite?.contact?.name +
            "(" +
            selectedSite?.company?.name +
            ")",
          job: selectedSite?.contact?.job,
          roles: selectedSite?.contact?.roles,
        },
        entityId: null,
        entityType: "site",
        id: "",
        relatedContactRoles: selectedSite?.contact?.roles,
        role: selectedSite?.contact?.job,
      });
      if (leadConatct.length > 0) setContactList(leadConatct);
      setLeadCompanyUpdated(true);
    }
  }, [selectedSite]);

  const onNameChange = (e) => {
    setFormData({
      ...formData,
      name: {
        value: e.target.value,
        error: "",
        required: true,
      },
    });
  };

  const onAddressSelect = (selectedAddress) => {
    const address = {
      street: selectedAddress.street.value,
      zipcode: selectedAddress.postal.value,
      city: selectedAddress.city.value,
      country: selectedAddress.countryId.value,
      position: {
        latitude: selectedAddress.latitude.value,
        longitude: selectedAddress.longitude.value,
      },
    };
    setFormData({
      ...formData,
      address: {
        value: address,
        error: "",
        required: true,
      },
    });
  };

  const onAddressSupplementChange = (e) => {
    setFormData({
      ...formData,
      addressSupplement: {
        value: e.target.value,
        error: "",
        required: true,
      },
    });
  };

  const onLatLngUpdate = (lat, lng) => {
    const newPosition = { ...formData.address.value.position };
    newPosition.latitude = lat;
    newPosition.longitude = lng;
    setFormData((formData) => {
      return {
        ...formData,
        address: {
          ...formData.address,
          value: {
            ...formData.address.value,
            position: newPosition,
          },
        },
      };
    });

    setPosition([]);
  };

  useEffect(() => {
    setFormData({
      ...formData,
      adminComment: {
        value: adminComment,
        error: "",
        required: true,
      },
    });
  }, [adminComment]);

  const onStartDateChange = (data) => {
    setFormData({
      ...formData,
      startDate: {
        value: data,
        error: "",
        required: true,
      },
    });
  };

  const onEndDateChange = (data) => {
    setFormData({
      ...formData,
      endDate: {
        value: data,
        error: "",
        required: true,
      },
    });
  };

  const onForecastClosingDateChange = (data) => {
    setFormData({
      ...formData,
      forecastClosingDate: {
        value: data,
        error: "",
        required: true,
      },
    });
  };

  const onCountryChange = (e) => {
    setFormData({
      ...formData,
      country: {
        value: e.value,
        error: "",
        required: true,
      },
      company: {
        value: "",
        error: "",
        required: true,
      },
      address: {
        value: "",
        error: "",
        required: true,
      },
    });
    setContactList([]);
    setResetMapAddress(true);
    setResetParentCompany(true);
  };

  useEffect(() => {
    if (resetMapAddress) setResetMapAddress(false);
    if (resetParentCompany) setResetParentCompany(false);
  }, [resetMapAddress, resetParentCompany]);

  const onCancel = () => {
    if (!isEditMode) history(`/site/list`);
    else {
      history(`/site/view/${selectedSite?.id}/general-info`);
    }
  };

  useEffect(() => {
    if (
      !!defaultCoordinates?.latitude &&
      !!defaultCoordinates?.longitude &&
      !isEditMode
    ) {
      initMapDrag(
        defaultCoordinates.latitude,
        defaultCoordinates.longitude,
        setPosition
      );
    }
  }, [defaultCoordinates]);

  useEffect(() => {
    if (
      !!formData.address?.value?.position?.latitude &&
      !!formData.address?.value?.position?.longitude
    ) {
      initMapDrag(
        formData.address?.value?.position?.latitude ?? "",
        formData.address?.value?.position?.longitude ?? "",
        setPosition
      );
    }
  }, [formData.address]);

  const updateRefeshStatus = () => {
    setIsRefreshList(true);
  };

  // validate form data
  const validateForm = (data) => {
    let isFormValid = true;
    if (!data.name.value) {
      isFormValid = false;
      data.name.error = t("FIELD_REQUIRED");
    }
    if (
      !isEditMode &&
      formData.id.value === null &&
      data.contacts.value.length === 0
    ) {
      isFormValid = false;
      data.contacts.error = t("SITE_CONTACT_MISSING");
    }
    if (data.company.value.length === 0) {
      isFormValid = false;
      data.company.error = t("FIELD_REQUIRED");
    }
    if (!data.startDate.value) {
      isFormValid = false;
      data.startDate.error = t("FIELD_REQUIRED");
    }
    if (!data.endDate.value) {
      isFormValid = false;
      data.endDate.error = t("FIELD_REQUIRED");
    }
    if (!data.forecastClosingDate.value) {
      isFormValid = false;
      data.forecastClosingDate.error = t("FIELD_REQUIRED");
    }
    if (data.address.value.length === 0) {
      isFormValid = false;
      data.address.error = t("FIELD_REQUIRED");
    }
    if (!data.loadingTime.value) {
      isFormValid = false;
      data.loadingTime.error = t("FIELD_REQUIRED");
    }
    if (!data.unloadingTime.value) {
      isFormValid = false;
      data.unloadingTime.error = t("FIELD_REQUIRED");
    }
    if (!data.simultaneousLoads.value) {
      isFormValid = false;
      data.simultaneousLoads.error = t("FIELD_REQUIRED");
    }
    if (!data.simultaneousUnloads.value) {
      isFormValid = false;
      data.simultaneousUnloads.error = t("FIELD_REQUIRED");
    }
    const listError = [...inputListError];
    const errorFieldName = "timeError";
    if (inputList.length > 0) {
      inputList.map((input, index) => {
        if (
          input.openingTime.hour() === "" ||
          input.openingTime.minute() === "" ||
          input.closingTime.hour() === "" ||
          input.closingTime.minute() === "" ||
          input.days.length === 0
        ) {
          isFormValid = false;
          listError[index][errorFieldName] = t(
            "WORKING_HOURS_MISSING_INFORMATION"
          );
          setRequiredTimeData("WORKING_HOURS_MISSING_INFORMATION");
        }
      });
      setInputListError(listError);
    }

    if (inputList.length === 0) {
      isFormValid = false;
      setRequiredTimeData("COMPANY_ERROR_MISSING_WORKING_HOURS");
    }

    if (!isFormValid) {
      setFormData({ ...data });
    }
    return isFormValid;
  };

  const getFormData = () => {
    const dataModified = new FormData();
    let workingHoursData = [];
    if (inputList.length > 0) {
      inputList.map((inputData) => {
        const newOpeningMint =
          parseInt(inputData.openingTime.minute(), 10) < 10
            ? "0" + inputData.openingTime.minute()
            : inputData.openingTime.minute();
        const newClosingMint =
          parseInt(inputData.closingTime.minute(), 10) < 10
            ? "0" + inputData.closingTime.minute()
            : inputData.closingTime.minute();
        workingHoursData.push({
          id: inputData.id,
          siteId: isEditMode ? selectedSite?.id : "",
          openingHour: inputData.openingTime.hour() + ":" + newOpeningMint,
          closingHour: inputData.closingTime.hour() + ":" + newClosingMint,
          days: inputData.days.join(),
        });
      });
    }
    dataModified.append("openingHours", JSON.stringify(workingHoursData));
    dataModified.append(
      "id",
      !leadId ? (selectedSite?.id ? selectedSite?.id : null) : null
    );
    dataModified.append("leadId", leadId);
    dataModified.append(
      "platformCountry",
      JSON.stringify({ id: formData?.country?.value })
    );
    dataModified.append("name", formData.name.value);
    dataModified.append("isTender", formData.isTender.value);
    dataModified.append(
      "company",
      JSON.stringify({
        id: formData.company.value.id,
        name: formData.company.value.name,
      })
    );
    dataModified.append("contacts", JSON.stringify(contactList));
    dataModified.append(
      "address",
      JSON.stringify({
        address: formData.address.value.street,
        zipcode: formData.address.value.zipcode,
        city: formData.address.value.city,
        country: formData.address.value.country,
        supplement: formData.addressSupplement.value,
        position: {
          latitude: formData.address.value.position.latitude,
          longitude: formData.address.value.position.longitude,
        },
      })
    );
    dataModified.append("startDate", formData.startDate.value);
    dataModified.append(
      "endDate",
      formData.endDate.value ? formData.endDate.value : formData.startDate.value
    );
    dataModified.append(
      "forecastClosingDate",
      formData.forecastClosingDate.value
        ? formData.forecastClosingDate.value
        : formData.forecastClosingDate.value
    );
    if (!isEditMode && formData.id.value === null)
      dataModified.append("status", 11);
    dataModified.append("source", "HS");
    dataModified.append("adminComment", formData.adminComment.value);
    dataModified.append(
      "unloadingTime",
      parseInt(formData.unloadingTime.value, 10)
    );
    dataModified.append(
      "simultaneousLoads",
      parseInt(formData.simultaneousLoads.value, 10)
    );
    dataModified.append(
      "simultaneousUnloads",
      parseInt(formData.simultaneousUnloads.value, 10)
    );
    dataModified.append(
      "loadingTime",
      parseInt(formData.loadingTime.value, 10)
    );
    dataModified.append("createdBy", userInfo.id);

    if (formData.documents.length > 0) {
      for (let i = 0; i < formData.documents.length; i++) {
        dataModified.append(`document[${i}][type][id]`, docType[i]);
        dataModified.append(
          `document[${i}][filename]`,
          formData.documents[i].name
        );
        dataModified.append(`document[${i}][file]`, formData.documents[i]);
      }
    }
    if (existingDocuments !== undefined && existingDocuments?.length > 0) {
      let i = formData.documents.length > 0 ? formData.documents.length : 0;
      existingDocuments.forEach((document) => {
        dataModified.append(`document[${i}][filename]`, document.fileName);
        dataModified.append(`document[${i}][id]`, document.id);
        dataModified.append(`document[${i}][file]`, document.fileName);
        i += 1;
      });
    }
    if (!isEditMode && (formData.id.value === null || leadId)) {
      if (!countryInfo.country?.needKickoff) {
        dataModified.append("needKickoff", 0);
      }
      if (countryInfo.country?.needKickoff) {
        const companyId = formData.company.value.id;
        if (allCompanyList?.length > 0) {
          const selectedCompany = allCompanyList.find(
            (company) => company.id === companyId
          );
          dataModified.append("needKickoff", selectedCompany?.needKickoff);
        }
        if (allCompanyList?.length === 0 && leadId) {
          dataModified.append(
            "needKickoff",
            selectedSite?.company?.needKickoff
          );
        }
      }
    }
    return dataModified;
  };

  const onSubmit = () => {
    const isFormValid = validateForm(formData);
    if (isFormValid) {
      setIsSiteCreating(true);
      setCreateSiteAPIError("");
      if ((!isEditMode && formData.id.value === null) || leadId) {
        axios
          .post(`/sites`, getFormData())
          .then((response) => {
            dispatch(addSingleSite(response.data));
            setSelectedSite(response?.data[0]);
            setIsSiteCreating(false);
            if (leadId) history(`/lead/view/${leadId}`);
            else setActiveTab("enquiry");
          })
          .catch((error) => {
            setIsSiteCreating(false);
            setCreateSiteAPIError(error.response?.data?.message ?? "");
          });
      } else {
        axios
          .post(`/sites/${selectedSite?.id}`, getFormData())
          .then((response) => {
            dispatch(addSingleSite(response.data));
            setIsSiteCreating(false);
            setSelectedSite(response?.data[0]);
            if (!isEditMode) setActiveTab("enquiry");
            else history(`/site/view/${response?.data[0].id}/general-info`);
          })
          .catch((error) => {
            setIsSiteCreating(false);
            setCreateSiteAPIError(error.response?.data?.message ?? "");
          });
      }
    }
  };

  return (
    <>
      {((isEditMode && selectedSite?.id && formData.id.value) ||
        !isEditMode) && (
        <OuterWrapperBackground>
          <PaddingDiv>
            <FormBody>
              <OuterWrapper>
                <FlexRow>
                  <SectionContainer>
                    <div>
                      <GridRow
                        column={
                          countryListForFilter?.length > 1
                            ? "13rem 1fr 1fr"
                            : "1fr 1fr"
                        }
                      >
                        {countryListForFilter?.length > 1 && (
                          <Dropdown
                            id="product-country"
                            label={t("COUNTRY")}
                            name="country"
                            value={formData.country.value}
                            onChange={onCountryChange}
                            defaultOption={t("COUNTRY")}
                            options={countryListForFilter}
                            labelStyles={
                              formData.country.error !== ""
                                ? labelStylesError
                                : labelStyles
                            }
                            inputStyles={
                              formData.country.error !== ""
                                ? inputStylesError
                                : inputStyles
                            }
                            required
                            disabled={
                              id === null ||
                              leadId ||
                              (selectedSite?.status &&
                                [1, 9, 11].includes(selectedSite?.status))
                                ? 0
                                : 1
                            }
                            error={formData.country.error}
                          />
                        )}
                        <TextInput
                          type="text"
                          label={t("SITE_NAME")}
                          onChange={onNameChange}
                          name="site-name"
                          id="site-name"
                          value={String(formData.name?.value) || ""}
                          isFloat
                          required
                          errorStyles={{ "font-style": "italic" }}
                          labelStyles={
                            formData.name.error !== ""
                              ? labelStylesError
                              : labelStyles
                          }
                          inputStyles={
                            formData.name.error !== ""
                              ? inputStylesError
                              : inputStyles
                          }
                          error={formData.name.error}
                          containerStyles={{ marginTop: "21px" }}
                          placeholder={t("SITE_NAME")}
                        />
                        <div>
                          {(!isEditMode ||
                            (selectedSite?.status &&
                              [1, 2, 3, 9, 11, 12].includes(
                                selectedSite?.status
                              ))) && (
                            <div style={{ lineHeight: "84px" }}>
                              <Image
                                src={
                                  formData.isTender.value
                                    ? enableIcon
                                    : disableIcon
                                }
                                alt={t("SITE_TOGGLE_IS_TENDER")}
                                title={t("SITE_TOGGLE_IS_TENDER")}
                                className="add-site__image-icon"
                                onClick={() => {
                                  setFormData({
                                    ...formData,
                                    isTender: {
                                      value: !formData.isTender.value,
                                      error: "",
                                      required: true,
                                    },
                                  });
                                }}
                              />
                              <label className="label center-products-list__active-label">
                                {t("SITE_TOGGLE_IS_TENDER")}
                              </label>
                            </div>
                          )}
                          {formData.id.value !== null &&
                            selectedSite?.status &&
                            !leadId &&
                            ![1, 2, 3, 9, 11, 12].includes(
                              selectedSite?.status
                            ) && (
                              <div style={{ lineHeight: "84px" }}>
                                <span style={{ opacity: "0.5" }}>
                                  <Image
                                    src={
                                      formData.isTender.value
                                        ? enableIcon
                                        : disableIcon
                                    }
                                    alt={t("SITE_TOGGLE_IS_TENDER")}
                                    title={t("SITE_TOGGLE_IS_TENDER")}
                                    className="add-site__image-icon"
                                  />
                                </span>

                                <label className="label center-products-list__active-label">
                                  {t("SITE_TOGGLE_IS_TENDER")}
                                </label>
                              </div>
                            )}
                        </div>
                      </GridRow>
                      <GridRow column="1fr 1fr">
                        <div>
                          <ParentCompany
                            formData={formData}
                            setFormData={setFormData}
                            isEditMode={formData.id.value !== null}
                            queryParamType={queryParamType}
                            objectType="company"
                            setAllCompanyList={setAllCompanyList}
                            isLeadCompanyUpdated={isLeadCompanyUpdated}
                            setLeadCompanyUpdated={setLeadCompanyUpdated}
                            leadId={leadId}
                            countryId={formData?.country?.value}
                            objectId={formData?.company?.value?.id}
                            resetParentCompany={resetParentCompany}
                            isEditable={
                              id === null ||
                              leadId ||
                              (selectedSite?.status &&
                                [1, 9, 11].includes(selectedSite?.status))
                            }
                          />
                        </div>
                        <GridRow column="1fr 1fr">
                          <div
                            className={
                              formData.startDate.error
                                ? "add-site__date-container_start_error"
                                : "add-site__date-container_start add-site__date-container_start_required"
                            }
                          >
                            <label htmlFor="winning-date">
                              {t("SITE_ESTIMATED_START_DATE")}
                            </label>
                            <DatePicker
                              style={{
                                padding: "5px 10px",
                                backgroundColor: "white",
                                borderColor: "hsl(0deg, 0%, 80%)",
                                borderRadius: "4px",
                                fontFamily: "Lato",
                                fontSize: "16px",
                                height: "auto",
                                width: "100%",
                                lineHeight: "1.4",
                                border: "1px solid #474747",
                              }}
                              disabled={
                                id !== null &&
                                selectedSite?.status &&
                                ![1, 2, 3, 9, 11].includes(selectedSite?.status)
                              }
                              title={t("SITE_ESTIMATED_START_DATE")}
                              className={{ ParentDatePicker }}
                              containerClassName={{ ParentDatePicker }}
                              calendarPosition="bottom-center"
                              onChange={(e) => {
                                onStartDateChange(
                                  moment(e.toDate()).format("YYYY-MM-DD")
                                );
                              }}
                              format={getDatePickerFormat()}
                              locale={getCalendarLocale()}
                              value={
                                getDateFormat(
                                  formData.startDate.value,
                                  "YYYY-MM-DD"
                                ) || ""
                              }
                              name="start-date"
                              // locale={getCalendarLocale()}
                            />
                            {formData.startDate.error && (
                              <RequiredMessage>
                                {formData.startDate.error}
                              </RequiredMessage>
                            )}
                          </div>
                          <div
                            className={
                              formData.startDate.error
                                ? "add-site__date-container_start_error"
                                : "add-site__date-container_start add-site__date-container_start_required"
                            }
                          >
                            <label htmlFor="winning-date">
                              {t("SITE_ESTIMATED_END_DATE")}
                            </label>
                            <DatePicker
                              style={{
                                padding: "5px 10px",
                                backgroundColor: "white",
                                borderColor: "hsl(0deg, 0%, 80%)",
                                borderRadius: "4px",
                                fontFamily: "Lato",
                                fontSize: "16px",
                                height: "auto",
                                width: "100%",
                                lineHeight: "1.4",
                                border: "1px solid #474747",
                              }}
                              title={t("SITE_ESTIMATED_END_DATE")}
                              className={{ ParentDatePicker }}
                              containerClassName={{ ParentDatePicker }}
                              calendarPosition="bottom-center"
                              disabled={
                                id !== null &&
                                selectedSite?.status &&
                                ![1, 2, 3, 9, 11].includes(selectedSite?.status)
                              }
                              onChange={(e) => {
                                onEndDateChange(
                                  moment(e.toDate()).format("YYYY-MM-DD")
                                );
                              }}
                              format={getDatePickerFormat()}
                              locale={getCalendarLocale()}
                              value={
                                getDateFormat(
                                  formData.endDate.value,
                                  "YYYY-MM-DD"
                                ) || ""
                              }
                              name="start-date"
                            />
                            {formData.endDate.error && (
                              <RequiredMessage>
                                {formData.endDate.error}
                              </RequiredMessage>
                            )}
                          </div>
                        </GridRow>
                      </GridRow>
                      <GridRow column="75% 1fr">
                        {!isEditMode &&
                          (!selectedSite?.id || selectedSite?.company?.id) && (
                            <div>
                              <div
                                className={
                                  formData?.contacts?.error
                                    ? "add-site__date-container_start_error"
                                    : "add-site__date-container_start add-site__date-container_start_required"
                                }
                              >
                                <label htmlFor="winning-date">
                                  {t("CONTACTS")}
                                </label>
                                <Image
                                  src={addIcon}
                                  alt="ADD_RELATED_CONTACT"
                                  onClick={addContact}
                                  className="add-site__add-icon"
                                />
                                <ContactContainer>
                                  {contactList.map((contact) => (
                                    <ContactButton
                                      key={contact.contact.id}
                                      border="#00AEEA"
                                      background="#00AEEA"
                                      marginRight="5px"
                                      marginTop="5px"
                                    >
                                      {contact.contact.name}
                                      <SpanButton
                                        key={contact.contact.id}
                                        border="#00AEEA"
                                        background="#00AEEA"
                                        onClick={() => {
                                          setContactList(
                                            contactList.filter(
                                              (a) =>
                                                a.contact.id !==
                                                contact.contact.id
                                            )
                                          );
                                        }}
                                      >
                                        <span aria-hidden="true">&times;</span>
                                      </SpanButton>
                                    </ContactButton>
                                  ))}
                                </ContactContainer>
                                {formData?.contacts?.error && (
                                  <RequiredMessage>
                                    {formData?.contacts?.error}
                                  </RequiredMessage>
                                )}
                              </div>
                            </div>
                          )}
                        {isEditMode &&
                          Object.keys(selectedSite).length !== 0 && <div></div>}
                        {!isEditMode &&
                          Object.keys(selectedSite).length !== 0 &&
                          selectedSite?.company?.name !== undefined && (
                            <div></div>
                          )}
                        <div
                          className={
                            formData.forecastClosingDate.error
                              ? "add-site__date-container_start_error"
                              : "add-site__date-container_start add-site__date-container_start_required"
                          }
                        >
                          <label htmlFor="winning-date">
                            {t("SITE_EXPECTED_CLOSING_DATE")}
                          </label>
                          <DatePicker
                            style={{
                              padding: "5px 10px",
                              backgroundColor: "white",
                              borderColor: "hsl(0deg, 0%, 80%)",
                              borderRadius: "4px",
                              fontFamily: "Lato",
                              fontSize: "16px",
                              height: "auto",
                              width: "100%",
                              lineHeight: "1.4",
                              border: "1px solid #474747",
                            }}
                            title={t("SITE_EXPECTED_CLOSING_DATE")}
                            className={{ ParentDatePicker }}
                            containerClassName={{ ParentDatePicker }}
                            calendarPosition="bottom-center"
                            disabled={
                              id !== null &&
                              selectedSite?.status &&
                              ![1, 2, 9, 11].includes(selectedSite?.status) &&
                              formData.forecastClosingDate.value
                            }
                            onChange={(e) => {
                              onForecastClosingDateChange(
                                moment(e.toDate()).format("YYYY-MM-DD")
                              );
                            }}
                            format={getDatePickerFormat()}
                            locale={getCalendarLocale()}
                            value={
                              getDateFormat(
                                formData.forecastClosingDate.value,
                                "YYYY-MM-DD"
                              ) || ""
                            }
                            name="start-date"
                          />
                          {formData.forecastClosingDate.error && (
                            <RequiredMessage>
                              {formData.forecastClosingDate.error}
                            </RequiredMessage>
                          )}
                        </div>
                      </GridRow>
                      <GridRow column="1fr 1fr">
                        {countryListAvailable && !isLeadJobsite && (
                          <AddressSearchWithMap
                            isFloat
                            position={position}
                            setPosition={setPosition}
                            title="SITE_ADDRESS"
                            icon={mapBlueIcon}
                            countryId={formData?.country?.value}
                            name="addAddress"
                            language={localStorage.getItem("locale")}
                            onAddressSelect={onAddressSelect}
                            onLatLngUpdate={onLatLngUpdate}
                            error={formData.address.error}
                            resetMapAddress={resetMapAddress}
                            defaultAddress={
                              formData.address.value.country
                                ? formData.address.value?.street +
                                  " " +
                                  formData.address.value?.zipcode +
                                  " " +
                                  formData.address.value?.city +
                                  " " +
                                  (formData.address.value?.country
                                    ? getCountryName(
                                        formData.address.value.country,
                                        countries
                                      )
                                    : "")
                                : ""
                            }
                          />
                        )}
                        {countryListAvailable &&
                          isLeadJobsite &&
                          formData.address.value?.country && (
                            <AddressSearchWithMap
                              isFloat
                              position={position}
                              setPosition={setPosition}
                              title="SITE_ADDRESS"
                              icon={mapBlueIcon}
                              countryId={formData?.country?.value}
                              name="addAddress"
                              language={localStorage.getItem("locale")}
                              onAddressSelect={onAddressSelect}
                              onLatLngUpdate={onLatLngUpdate}
                              error={formData.address.error}
                              resetMapAddress={resetMapAddress}
                              defaultAddress={
                                formData.address.value.country
                                  ? formData.address.value?.street +
                                    " " +
                                    formData.address.value?.zipcode +
                                    " " +
                                    formData.address.value?.city +
                                    " " +
                                    (formData.address.value?.country
                                      ? getCountryName(
                                          formData.address.value.country,
                                          countries
                                        )
                                      : "")
                                  : ""
                              }
                            />
                          )}
                        <TextInput
                          type="text"
                          label=""
                          name="company-address-supplement"
                          id="company-address-number"
                          value={
                            String(formData.addressSupplement?.value) || ""
                          }
                          isFloat
                          onChange={onAddressSupplementChange}
                          labelStyles={labelStyles}
                          containerStyles={{ marginTop: "30px" }}
                          inputStyles={{
                            padding: "7px 5px 7px 10px",
                          }}
                          disabled={
                            id !== null &&
                            selectedSite?.status &&
                            ![1, 2, 9, 11].includes(selectedSite?.status)
                          }
                          error={formData.addressSupplement.error}
                          placeholder={t("ADDRESS_SUPPLEMENT")}
                        />
                      </GridRow>
                      <GridRow>
                        <MapContainer id="map" />
                      </GridRow>
                      <GridRow column="1fr">
                        <div>
                          <ContainerLabel htmlFor="description">
                            {t("SITE_CLIENT_INFORMATION")}
                          </ContainerLabel>
                          <CKEditorText
                            descriptionData={adminComment}
                            setDescriptionData={setAdminComment}
                          />
                          {formData.adminComment.error && (
                            <RequiredMessage>
                              {formData.adminComment.error}
                            </RequiredMessage>
                          )}
                        </div>
                      </GridRow>
                      <GridRow column="1fr 1fr">
                        <GridRow column="1fr 1fr">
                          <div>
                            <TextInput
                              type="text"
                              label={t("LOADING_TIME")}
                              name="centre-loading-time"
                              id="centre-loading-time"
                              value={String(formData.loadingTime?.value) || ""}
                              isFloat
                              onChange={(e) => {
                                setFormData({
                                  ...formData,
                                  loadingTime: {
                                    value: e.target.value,
                                    error: "",
                                    required: true,
                                  },
                                });
                              }}
                              labelStyles={labelStyles}
                              containerStyles={{ marginTop: "21px" }}
                              inputStyles={{ padding: "7px 5px 7px 10px" }}
                              error={formData.loadingTime.error}
                              placeholder={t("LOADING_TIME")}
                            />
                            <label className="label center-products-list__active-label">
                              min.
                            </label>
                          </div>

                          <div>
                            <TextInput
                              type="text"
                              label={t("UNLOADING_TIME")}
                              name="centre-unloading-time"
                              id="centre-unloading-time"
                              value={
                                String(formData.unloadingTime?.value) || ""
                              }
                              isFloat
                              onChange={(e) => {
                                setFormData({
                                  ...formData,
                                  unloadingTime: {
                                    value: e.target.value,
                                    error: "",
                                    required: true,
                                  },
                                });
                              }}
                              labelStyles={labelStyles}
                              containerStyles={{ marginTop: "21px" }}
                              inputStyles={{ padding: "7px 5px 7px 10px" }}
                              error={formData.unloadingTime.error}
                              placeholder={t("UNLOADING_TIME")}
                            />
                            <label className="label center-products-list__active-label">
                              min.
                            </label>
                          </div>
                        </GridRow>
                        <GridRow column="1fr 1fr">
                          <TextInput
                            type="text"
                            label={t("SIMULTANEOUS_LOADS")}
                            name="centre-simultaneous-loads"
                            id="centre-simultaneous-loads"
                            value={
                              String(formData.simultaneousLoads?.value) || ""
                            }
                            isFloat
                            onChange={(e) => {
                              setFormData({
                                ...formData,
                                simultaneousLoads: {
                                  value: e.target.value,
                                  error: "",
                                  required: true,
                                },
                              });
                            }}
                            labelStyles={labelStyles}
                            containerStyles={{ marginTop: "21px" }}
                            inputStyles={{ padding: "7px 5px 7px 10px" }}
                            error={formData.simultaneousLoads.error}
                            placeholder={t("COMPANY_PHONE")}
                          />
                          <TextInput
                            type="text"
                            label={t("SIMULTANEOUS_UNLOADS")}
                            name="centre-simultaneous-unloads"
                            id="centre-simultaneous-unloads"
                            value={
                              String(formData.simultaneousUnloads?.value) || ""
                            }
                            isFloat
                            onChange={(e) => {
                              setFormData({
                                ...formData,
                                simultaneousUnloads: {
                                  value: e.target.value,
                                  error: "",
                                  required: true,
                                },
                              });
                            }}
                            labelStyles={labelStyles}
                            containerStyles={{ marginTop: "21px" }}
                            inputStyles={{ padding: "7px 5px 7px 10px" }}
                            error={formData.simultaneousUnloads.error}
                            placeholder={t("SIMULTANEOUS_UNLOADS")}
                          />
                        </GridRow>
                      </GridRow>
                      <AddEditWorkingHours
                        inputList={inputList}
                        inputListError={inputListError}
                        setInputList={setInputList}
                        setInputListError={setInputListError}
                        requiredTimeData={requiredTimeData}
                        setRequiredTimeData={setRequiredTimeData}
                        workingHours={
                          formData.workingHours.value
                            ? formData.workingHours.value
                            : []
                        }
                      />
                      {!isEditMode &&
                        (Object.keys(selectedSite).length === 0 || leadId) && (
                          <DocumentUploadWithType
                            isEditMode={isEditMode}
                            setFormData={setFormData}
                            formData={formData}
                            selectedProduct={selectedSite}
                            existingDocuments={existingDocuments}
                            setExistingDocuments={setExistingDocuments}
                            setDocType={setDocType}
                            docType={docType}
                            documentTypeOptions={siteDocumentTypes}
                            docFromLead={leadId ? 1 : 0}
                          />
                        )}
                    </div>
                  </SectionContainer>
                </FlexRow>
              </OuterWrapper>
            </FormBody>
            <ButtonWrapper>
              {createSiteAPIError && <div>{createSiteAPIError}</div>}
              <Button
                styles={{
                  color: "#333",
                  background: "#fff",
                  border: "1px solid #ccc",
                  marginRight: "0.32rem",
                  "&:hover": {
                    backgroundColor: "#ccc",
                  },
                }}
                onClick={onCancel}
              >
                <>{t("CANCEL")}</>
              </Button>

              <Button
                styles={{
                  color: "white",
                  background: "#1BACE4",
                  border: "1px solid #1BACE4",
                  marginRight: "0.32rem",
                  "&:hover": {
                    backgroundColor: "#1589b6",
                  },
                }}
                onClick={onSubmit}
              >
                <>
                  {isSiteCreating && <ClipLoader size={15} color="#fff" />}
                  {!isSiteCreating && t("SUBMIT")}
                </>
              </Button>
            </ButtonWrapper>
          </PaddingDiv>
          {isEditContactPopupVisible && (
            <AddEditRelatedContact
              title={t("RELATED_CONTACT")}
              onClose={closeContactEditPopup}
              updateRefeshStatus={updateRefeshStatus}
              companyId={formData.company.value.id}
              countryId={formData.country.value}
              entityType="site"
              entityId={null}
              onAddForCreate={addContactToList}
              dependancies={JSON.stringify(["roles"])}
            />
          )}
        </OuterWrapperBackground>
      )}
    </>
  );
};
Site.propTypes = {
  t: PropTypes.func.isRequired,
  id: PropTypes.number,
  leadId: PropTypes.number,
  selectedSite: PropTypes.object.isRequired,
  setSelectedSite: PropTypes.func.isRequired,
  isEditMode: PropTypes.bool,
  setActiveTab: PropTypes.func.isRequired,
  isLeadJobsite: PropTypes.bool,
};
Site.defaultProps = {
  id: null,
  leadId: null,
  isEditMode: false,
  isLeadJobsite: false,
};

export default withTranslation()(Site);
